import { z } from 'zod';
import { Button } from '../ui/Button';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

export type CommsMethod = 'PHONE' | 'RADIO' | 'SIGNAL' | 'SATELLITE' | 'OTHER';

export interface CommsContact {
  name: string;
  contact: string;
}

export interface CommsChannel {
  method: CommsMethod;
  channelDetail?: string;
  contacts: CommsContact[];
}

export interface CommunicationPlanCodeWord {
  word: string;
  meaning: string;
}

export interface CommunicationPlan {
  primary: CommsChannel;
  secondary?: CommsChannel;
  codeWords: CommunicationPlanCodeWord[];
  notes?: string;
}

const commsContactSchema = z.object({
  name: z.string().min(1, 'Contact name is required').max(100),
  contact: z.string().min(1, 'Phone number or callsign is required').max(100),
});

const commsChannelSchema = z.object({
  method: z.enum(['PHONE', 'RADIO', 'SIGNAL', 'SATELLITE', 'OTHER']),
  channelDetail: z.string().max(200).optional(),
  contacts: z.array(commsContactSchema).min(1, 'At least one contact is required'),
});

export const communicationPlanSchema = z.object({
  primary: commsChannelSchema,
  secondary: commsChannelSchema.optional(),
  codeWords: z.array(z.object({
    word: z.string().min(1, 'Code word is required').max(50),
    meaning: z.string().min(1, 'Meaning is required').max(200),
  })),
  notes: z.string().max(1000).optional(),
});

export const COMMS_METHOD_OPTIONS: Array<{ value: CommsMethod; label: string }> = [
  { value: 'PHONE', label: 'Phone' },
  { value: 'RADIO', label: 'Radio' },
  { value: 'SIGNAL', label: 'Signal' },
  { value: 'SATELLITE', label: 'Satellite' },
  { value: 'OTHER', label: 'Other' },
];

export const emptyChannel = (): CommsChannel => ({
  method: 'PHONE',
  channelDetail: '',
  contacts: [{ name: '', contact: '' }],
});

export const emptyPlan = (): CommunicationPlan => ({
  primary: emptyChannel(),
  codeWords: [],
});

/** First validation problem in a plan, as a human-readable message, or null when valid. */
export function validateCommunicationPlan(plan: CommunicationPlan): string | null {
  const result = communicationPlanSchema.safeParse(plan);
  if (result.success) { return null; }
  const issue = result.error.issues[0];
  return issue ? issue.message : 'Communication plan is incomplete';
}

interface CommunicationPlanEditorProps {
  value: CommunicationPlan | null;
  onChange: (plan: CommunicationPlan | null) => void;
  /** Selected venue's security contact, offered as a one-click prefill row */
  venueSecurityContact?: { name: string; phone: string } | null;
  /** Validation error to surface inside the editor card */
  error?: string | null;
}

const inputClass =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 text-secondary-900 dark:text-white';

const CommunicationPlanEditor: React.FC<CommunicationPlanEditorProps> = ({
  value,
  onChange,
  venueSecurityContact = null,
  error = null,
}) => {
  if (!value) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => onChange(emptyPlan())} data-testid="add-comms-plan">
        <PlusIcon className="w-4 h-4 mr-1" />
        Add Communication Plan
      </Button>
    );
  }

  const updateChannel = (key: 'primary' | 'secondary', channel: CommsChannel) => {
    onChange({ ...value, [key]: channel });
  };

  const renderChannel = (key: 'primary' | 'secondary', channel: CommsChannel) => (
    <div className="space-y-3 border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50" data-testid={`comms-${key}`}>
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          {key === 'primary' ? 'Primary Comms' : 'Secondary Comms (fallback)'}
        </h5>
        {key === 'secondary' && (
          <Button type="button" variant="outline" size="sm" onClick={() => onChange({ ...value, secondary: undefined })}>
            <TrashIcon className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <label className="block text-sm font-medium mb-1">Method</label>
          <select
            value={channel.method}
            onChange={(e) => updateChannel(key, { ...channel, method: e.target.value as CommsMethod })}
            className={`w-full ${inputClass}`}
            data-testid={`comms-${key}-method`}
          >
            {COMMS_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Channel Detail</label>
          <input
            type="text"
            value={channel.channelDetail ?? ''}
            onChange={(e) => updateChannel(key, { ...channel, channelDetail: e.target.value })}
            placeholder='e.g., "Signal Chat: EP Op 21" or "Radio Ch. 4 / 462.5625 MHz"'
            className={`w-full ${inputClass}`}
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium">Agent Contacts</label>
          <div className="flex gap-2">
            {venueSecurityContact && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  updateChannel(key, {
                    ...channel,
                    contacts: [...channel.contacts, { name: venueSecurityContact.name, contact: venueSecurityContact.phone }],
                  })
                }
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Add venue security contact
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => updateChannel(key, { ...channel, contacts: [...channel.contacts, { name: '', contact: '' }] })}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Contact
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          {channel.contacts.map((contact, index) => (
            <div key={index} className="flex items-center gap-2">
              <input
                type="text"
                value={contact.name}
                onChange={(e) => {
                  const contacts = [...channel.contacts];
                  contacts[index] = { ...contacts[index], name: e.target.value };
                  updateChannel(key, { ...channel, contacts });
                }}
                placeholder="Agent name"
                className={`flex-1 ${inputClass}`}
              />
              <input
                type="text"
                value={contact.contact}
                onChange={(e) => {
                  const contacts = [...channel.contacts];
                  contacts[index] = { ...contacts[index], contact: e.target.value };
                  updateChannel(key, { ...channel, contacts });
                }}
                placeholder="Phone # or callsign"
                className={`flex-1 ${inputClass}`}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  updateChannel(key, { ...channel, contacts: channel.contacts.filter((_, i) => i !== index) })
                }
              >
                <TrashIcon className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="comms-plan-editor">
      {error && (
        <p className="text-red-500 text-sm" data-testid="comms-plan-error">{error}</p>
      )}

      {renderChannel('primary', value.primary)}

      {value.secondary ? (
        renderChannel('secondary', value.secondary)
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => onChange({ ...value, secondary: emptyChannel() })}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Add Secondary Channel
        </Button>
      )}

      {/* Code Words */}
      <div className="space-y-3 border rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50" data-testid="comms-code-words">
        <div className="flex items-center justify-between">
          <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Code Words</h5>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange({ ...value, codeWords: [...value.codeWords, { word: '', meaning: '' }] })}
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Code Word
          </Button>
        </div>
        {value.codeWords.map((codeWord, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={codeWord.word}
              onChange={(e) => {
                const codeWords = [...value.codeWords];
                codeWords[index] = { ...codeWords[index], word: e.target.value };
                onChange({ ...value, codeWords });
              }}
              placeholder="Code word"
              className={`w-1/3 ${inputClass}`}
            />
            <input
              type="text"
              value={codeWord.meaning}
              onChange={(e) => {
                const codeWords = [...value.codeWords];
                codeWords[index] = { ...codeWords[index], meaning: e.target.value };
                onChange({ ...value, codeWords });
              }}
              placeholder="Meaning (e.g., Immediate evacuation)"
              className={`flex-1 ${inputClass}`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onChange({ ...value, codeWords: value.codeWords.filter((_, i) => i !== index) })}
            >
              <TrashIcon className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-medium mb-1">Notes</label>
        <textarea
          value={value.notes ?? ''}
          onChange={(e) => onChange({ ...value, notes: e.target.value })}
          rows={2}
          placeholder="Additional comms notes (optional)"
          className={`w-full ${inputClass}`}
        />
      </div>

      <Button type="button" variant="outline" size="sm" onClick={() => onChange(null)}>
        <TrashIcon className="w-4 h-4 mr-1" />
        Remove Communication Plan
      </Button>
    </div>
  );
};

export default CommunicationPlanEditor;
